import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { ROUTER_REQUEST, RouterRequestAction } from '@ngrx/router-store';
import { State, Store } from '@ngrx/store';
import { BugsnagService, CacheKeyConstants, CacheService, isNullOrUndefined } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import { ReplaySubject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import * as fromDriverOnboardingListActions from '../../store/driver-onboarding-list/driver-onboarding-list.actions';
import { driverOnboardingListState } from '../../store/driver-onboarding-list/driver-onboarding-list.selectors';
import * as fromDriverOnboardingProfileActions from '../../store/driver-onboarding-profile/driver-onboarding-profile.actions';
import { driverOnboardingProfileState } from '../../store/driver-onboarding-profile/driver-onboarding-profile.selectors';
import * as fromRideTracking from '../../store/ride-tracking';
import * as fromRideMonitoring from '../../store/ride-monitoring';
import { GetTripDetailFromUrl, RestoreState } from '../../store/trip-detail/trip-detail.actions';
import { getTripDetailState } from '../../store/trip-detail/trip-detail.selectors';
import * as fromDriverActions from '../../store/driver/driver.actions';
import { driverState } from '../../store/driver/driver.selectors';
import * as organizationRider from '../../store/organization-rider/organization-rider.actions';
import { OrganizationRiderState } from '../../store/organization-rider/organization-rider.reducer';
import { getNewItineraryStarted, organizationRiderState } from '../../store/organization-rider/organization-rider.selectors';
import * as fromTransitProgramActions from '../../store/transit-program/transit-program.actions';
import { transitProgramState } from '../../store/transit-program/transit-program.selectors';
import * as fromFareReviewQueueActions from '../../store/fare-review-queue/fare-review-queue.actions';
import * as fromProviderActions from '../../store/dispatch/dispatch.actions';
import { getDispatchState } from '../../store/dispatch/dispatch.selectors';
import { fareReviewQueueState } from '../../store/fare-review-queue/fare-review-queue.selectors';

export interface Tab {
  route: string;
  name?: string;
  matIcon?: string;
  svgIcon?: string;
  navigationExtras?: NavigationExtras;
  disableClose?: boolean;
}

interface TabState {
  state: any;
  expirationDate: Date;
}

@Injectable({ providedIn: 'root' })
export class TabService {
  private _tabs: Array<Tab>;
  private _tabsState = new Map<string, TabState>();
  private _currentTab: Tab;
  tabs$ = new ReplaySubject<Array<Tab>>(1);
  private subscription: Subscription;

  constructor(
    private actions$: Actions,
    private authDataService: AuthDataService,
    private bugsnagService: BugsnagService,
    private cacheService: CacheService,
    private dialog: MatDialog,
    private router: Router,
    private store$: Store<State<any>>
  ) {
    this.authDataService.loginEvents.subscribe(() => {
      this._tabs = JSON.parse(this.cacheService.getCachedData(CacheKeyConstants.OPEN_TABS)) || this.getInitialTabs();
      this.bugsnagService.setUser(this.authDataService.currentUser);

      // handle removing a success page
      // TODO: handle this better, maybe we can restore that state somehow
      this._tabs = this._tabs.filter(
        tab => !tab.route.includes('new-itinerary/success') && !tab.route.includes('new-itinerary/ride-subscription-success')
      );

      this.tabs$.next(this._tabs);

      // handle navigation changes
      this.subscription = this.actions$.pipe(ofType<RouterRequestAction>(ROUTER_REQUEST)).subscribe(request => {
        const [fromUrl, fromParams] = request.payload.routerState.url.split('?');
        const [toUrl, toParams] = request.payload.event.url.split('?');
        const tabParams = this.paramsObjectFromURLSearchString(toParams);

        this.storeState(fromUrl);
        this.restoreState(toUrl, fromUrl);

        // fromUrl === '' is the special case of starting the app, so handle that
        if (fromUrl === '') {
          // handle removing a success page
          // TODO: handle this better, maybe we can restore that state somehow
          if (toUrl.includes('new-itinerary/success') || toUrl.includes('new-itinerary/ride-subscription-success')) {
            this.router.navigate(['new-itinerary']);
          } else {
            this._currentTab = this._tabs.find(tab => tab.route === toUrl);
            if (isNullOrUndefined(this._currentTab)) {
              this.addTab({ route: toUrl, navigationExtras: { queryParams: tabParams } });
            }
          }
        } else {
          this._currentTab = this._tabs.find(tab => tab.route === fromUrl);
        }

        // handle navigating within a tab
        if (this._tabs.findIndex(tab => tab.route === toUrl.split('?')[0]) === -1) {
          if (this._currentTab) {
            if (toUrl.includes('?')) {
              this._currentTab.navigationExtras = { queryParams: tabParams };
            }
            // break this out
            if (this.routeIsInTabbingContext(toUrl)) {
              this._currentTab.route = toUrl.split('?')[0];
            }
          }
        }

        this.tabs$.next(this._tabs);
        this._currentTab = this._tabs.find(tab => tab.route === toUrl);
        this.cacheService.cacheData(CacheKeyConstants.OPEN_TABS, JSON.stringify(this._tabs), 1);
      });
    });

    // handle clearing tabs on logout
    this.authDataService.logoutEvents.subscribe(() => {
      this.cacheService.removeCachedData(CacheKeyConstants.OPEN_TABS);
      this._tabsState.clear();
      this._tabs = this.getInitialTabs();
      this.tabs$.next(this._tabs);
      if (!isNullOrUndefined(this.subscription)) {
        this.subscription.unsubscribe();
      }
    });
  }

  getCurrentTab(): Tab {
    return this._currentTab;
  }

  addTab(tab?: Tab): void {
    if (!tab) {
      tab = {
        name: 'New tab',
        route: '/'
      };
    }

    if (!this.allowDuplicateTab(tab)) {
      this.router.navigate([tab.route], tab.navigationExtras);
      return;
    }

    this._tabs.push(tab);
    this.tabs$.next(this._tabs);
    this.router.navigate([tab.route], tab.navigationExtras);
  }

  closeTab(route: string, closedTabIndex: number, isActive: boolean): void {
    // wipe the state of new itinerary on close
    if (this._tabs[closedTabIndex].route.includes('/new-itinerary')) {
      this.store$
        .select(getNewItineraryStarted)
        .pipe(take(1))
        .subscribe(started => {
          if (started) {
            this.dialog
              .open(ConfirmationDialogComponent, {
                data: {
                  title: 'Are you sure?',
                  message: 'You will lose all progress'
                }
              })
              .afterClosed()
              .subscribe(confirmed => {
                if (confirmed) {
                  this._tabsState.delete('/new-itinerary');
                  this.store$.dispatch(organizationRider.Close({ payload: { done: true } }));
                  this.cleanupClosingTab(closedTabIndex, isActive, route);
                } else {
                  return;
                }
              });
          } else {
            this.cleanupClosingTab(closedTabIndex, isActive, route);
          }
        });
    } else if (this._tabs[closedTabIndex].route === '/onboarding/drivers') {
      this.store$.dispatch(fromDriverOnboardingListActions.RestoreDefaultState());
      this.cleanupClosingTab(closedTabIndex, isActive, route);
    } else {
      this.cleanupClosingTab(closedTabIndex, isActive, route);
    }
  }

  updateTab(options: { name: string; svgIcon?: string; matIcon?: string }): void {
    const { name, svgIcon, matIcon } = options;
    if (this._currentTab) {
      this._currentTab.name = name;
      this._currentTab.svgIcon = svgIcon;
      this._currentTab.matIcon = matIcon;
      this.tabs$.next(this._tabs);
    }
  }

  private cleanupClosingTab(closedTabIndex: number, shouldNavigateTab: boolean, route: string) {
    this._tabs.splice(closedTabIndex, 1);
    this.tabs$.next(this._tabs);
    this._tabsState.delete(route);
    if (shouldNavigateTab) {
      let nextTab = this._tabs[closedTabIndex - 1];
      if (!nextTab) {
        nextTab = this._tabs[closedTabIndex + 1];
      }
      this.router.navigate([nextTab.route], nextTab.navigationExtras);
    }
  }

  private restoreState(toUrl: string, fromUrl: string) {
    if (toUrl.includes('rides/')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(RestoreState({ payload: this._tabsState.get(toUrl).state }));
      } else {
        this.store$.dispatch(GetTripDetailFromUrl({ payload: toUrl }));
      }
    } else if (toUrl.includes('new-itinerary') || toUrl.includes('riders')) {
      // TODO (phill2mj): don't love all this... in the future, states should implement
      // and interface for restoring state (and handling weird updates like this) in a
      // way that blinds tabbing to that complexity
      if (this._tabsState.has(toUrl)) {
        if (toUrl.includes('new-itinerary')) {
          // if its itinerary then by default restory the state from /new-itinerary url
          this.store$.dispatch(organizationRider.RestoreState({ state: this._tabsState.get('/new-itinerary').state }));
          const organizationRiderState = this._tabsState.get(toUrl).state as OrganizationRiderState;
          // there is a chance the rider was updated if there is organization rider detail tab open with the same rider  (make the update)
          if (organizationRiderState.rider && !toUrl.includes('success')) {
            this._tabsState.forEach((tabState: TabState, key: string) => {
              if (
                key.includes(
                  `riders/details/${organizationRiderState.rider.organizationRiderId}/organization/${organizationRiderState.rider.organizationId}`
                )
              ) {
                this.store$.dispatch(organizationRider.GetOrganizationRiderDetailSuccess({ payload: tabState.state.rider }));
              }
            });
          }
        }

        if (toUrl.includes('riders')) {
          this._tabsState.forEach((tabState: TabState, key: string) => {
            if (key.includes(`new-itinerary/`) && this._tabsState.get(toUrl).state.rider === tabState.state.rider) {
              this.store$.dispatch(organizationRider.RestoreState({ state: tabState.state }));
            } else if (key.includes(`details/`)) {
              this.store$.dispatch(organizationRider.RestoreState({ state: this._tabsState.get(toUrl).state }));
            }
          });
        }
      }
    } else if (toUrl.includes('ride-tracking')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromRideTracking.RestoreState({ state: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('driver-management')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromDriverActions.RestoreState({ state: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('onboarding/drivers/')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromDriverOnboardingProfileActions.RestoreState({ state: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('onboarding/drivers')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromDriverOnboardingListActions.RestoreState({ state: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('providers') && toUrl.includes('rides')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromProviderActions.restoreState({ state: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('transit-program-management')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromTransitProgramActions.RestoreState({ transitProgramState: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('ride-monitoring')) {
      if (this._tabsState.has(toUrl) && fromUrl !== toUrl) {
        this.store$.dispatch(fromRideMonitoring.RestoreState({ state: this._tabsState.get(toUrl).state }));
      }
    } else if (toUrl.includes('fare/queue')) {
      if (this._tabsState.has(toUrl)) {
        this.store$.dispatch(fromFareReviewQueueActions.RestoreState({ state: this._tabsState.get(toUrl).state }));
      }
    }
  }

  private storeState(fromUrl: string) {
    if (fromUrl.includes('rides/')) {
      this.store$
        .select(getTripDetailState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('new-itinerary') || fromUrl.includes('riders/')) {
      this.store$
        .select(organizationRiderState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('ride-tracking')) {
      this.store$
        .select(fromRideTracking.rideTrackingState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('ride-monitoring')) {
      this.store$
        .select(fromRideMonitoring.rideMonitoringState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('driver-management')) {
      this.store$
        .select(driverState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('onboarding/drivers/')) {
      this.store$
        .select(driverOnboardingProfileState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('onboarding/drivers')) {
      this.store$
        .select(driverOnboardingListState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('providers') && fromUrl.includes('rides')) {
      this.store$
        .select(getDispatchState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('transit-program-management')) {
      this.store$
        .select(transitProgramState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    } else if (fromUrl.includes('fare/queue')) {
      this.store$
        .select(fareReviewQueueState)
        .pipe(take(1))
        .subscribe(state => {
          this._tabsState.set(fromUrl, { state: state, expirationDate: new Date() });
        });
    }
  }

  private getInitialTabs(): Array<Tab> {
    return [
      {
        name: 'Home',
        route: '/',
        svgIcon: 'uz_home',
        disableClose: true
      }
    ];
  }

  private routeIsInTabbingContext(route: string) {
    return !route.includes('maintenance-mode') && !route.includes('deprecation-mode') && !route.includes('login');
  }

  /**
   * These cases are to prevent copies of certain tabs. Returns false if the tab cannot be duplicated
   *
   * @param tab
   */
  private allowDuplicateTab(tab?: Tab): boolean {
    if (tab.route === '/' && this._tabs.some(t => t.route === '/')) {
      return false;
    }

    if (tab.route === '/rides' && this._tabs.some(t => t.route === '/rides')) {
      return false;
    }

    if (tab.route === '/new-itinerary' && this._tabs.findIndex(t => t.route === '/new-itinerary') > -1) {
      return false;
    }

    if (this._tabs.some(t => t.route === tab.route)) {
      return false;
    }

    return true;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  private paramsObjectFromURLSearchString(urlSearchString: string): object {
    if (urlSearchString) {
      return JSON.parse('{"' + decodeURI(urlSearchString).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"') + '"}');
    }
    return null;
  }
}
